import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';

/**
 * OfflineDetector - Monitors network status and shows overlay when offline
 * Prevents users from using the app without internet to avoid data loss
 */
const OfflineDetector = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showRetrying, setShowRetrying] = useState(false);

  useEffect(() => {
    // Handler for when connection is lost
    const handleOffline = () => {
      console.log('🔴 OFFLINE: No internet connection detected');
      setIsOnline(false);
    };

    // Handler for when connection is restored
    const handleOnline = () => {
      console.log('🟢 ONLINE: Internet connection restored');
      setIsOnline(true);
      setShowRetrying(false);
    };

    // Listen to browser online/offline events
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    // Additional check: periodically ping backend to verify real connectivity
    // (Browser can sometimes report "online" but backend is unreachable)
    // DISABLED FOR NOW: We are working on frontend-only Firebase features and backend might be down.
    /*
    const connectivityCheck = setInterval(async () => {
      if (navigator.onLine) {
        try {
          // Try to reach backend with short timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/health`, {
            method: 'GET',
            signal: controller.signal,
            cache: 'no-cache'
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error('Backend unreachable');
          }

          // Backend is reachable
          if (!isOnline) {
            console.log('🟢 Connectivity check: Backend reachable');
            setIsOnline(true);
            setShowRetrying(false);
          }
        } catch (error) {
          // Backend unreachable - treat as offline
          if (isOnline && error.name !== 'AbortError') {
            console.log('🔴 Connectivity check: Backend unreachable');
            setIsOnline(false);
          }
        }
      }
    }, 10000); // Check every 10 seconds
    */

    // Cleanup
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      // clearInterval(connectivityCheck);
    };
  }, [isOnline]);

  // Handler for manual retry
  const handleRetry = async () => {
    setShowRetrying(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/health`, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-cache'
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        console.log('🟢 Manual retry: Connection successful');
        setIsOnline(true);
        setShowRetrying(false);
      } else {
        throw new Error('Connection failed');
      }
    } catch (error) {
      console.log('🔴 Manual retry: Connection failed');
      setTimeout(() => setShowRetrying(false), 2000);
    }
  };

  // Don't render anything if online
  if (isOnline) {
    return null;
  }

  // Render offline overlay
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-slate-50 via-[#F7EEEB] to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
          backgroundSize: '40px 40px',
          color: 'rgb(59, 130, 246)' // [#B2545E]
        }}></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-md mx-4 text-center">
        {/* Animated WiFi Off Icon */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            {/* Pulsing background circle */}
            <div className="absolute inset-0 rounded-full bg-red-500 opacity-20 animate-ping"></div>
            <div className="absolute inset-0 rounded-full bg-red-500 opacity-10 animate-pulse"></div>

            {/* Icon */}
            <div className="relative bg-red-500/20 backdrop-blur-sm rounded-full p-8 border-4 border-red-500/30">
              <WifiOff className="w-20 h-20 text-red-500 dark:text-red-400 animate-pulse" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 drop-shadow-lg">
          No Internet Connection
        </h1>

        {/* Description */}
        <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
          Time Pavilion requires an active internet connection to prevent data loss and ensure your work is properly saved.
        </p>

        {/* Retry Button */}
        <button
          onClick={handleRetry}
          disabled={showRetrying}
          className="group relative px-8 py-4 bg-gradient-to-r from-[#B2545E] to-[#9A313B] hover:from-[#9A313B] hover:to-[#7C2730] text-white font-semibold rounded-xl shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          <span className="flex items-center justify-center gap-3">
            <RefreshCw
              className={`w-5 h-5 ${showRetrying ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`}
            />
            <span>{showRetrying ? 'Checking Connection...' : 'Retry Connection'}</span>
          </span>

          {/* Button glow effect */}
          <div className="absolute inset-0 rounded-xl bg-[#C87781] opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300"></div>
        </button>

        {/* Status indicator */}
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
          <span>Waiting for connection...</span>
        </div>
      </div>

      {/* Animated particles (optional decoration) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full opacity-20 animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${10 + Math.random() * 10}s`
            }}
          ></div>
        ))}
      </div>
    </div>
  );
};

export default OfflineDetector;
